/**
 * ftk-atc-v17.js — ATC-Modul für Farbtonfinder V17
 * Stellt window.FTKATC bereit.
 * Vertrag: resolveVariant, addToCart, openCartDrawer
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  resolveVariant                                                       */
  /* ------------------------------------------------------------------ */
  /**
   * Replikation der Logik aus pdp-hero-v2 applyFarbtonState (~Z.1096-1158)
   * + findVariant (~Z.876-882).
   *
   * Varianten-Shape (wie vom Liquid JSON):
   *   { id, option1, option2, option3, price, available, title }
   *   price ist INTEGER in Cent (Shopify .js-Endpunkt liefert Ganzzahl-Cent).
   *
   * @param {object} p
   * @param {Array}  p.variants   — Shopify product.variants (option1/option2/option3)
   * @param {string} p.klasse     — '1'..'4' (PC-Klasse)
   * @param {string} p.currentO2  — aktuell gewähltes Gebinde (option2, z.B. '5 L')
   * @param {string} p.currentO3  — aktuell gewählte Körnung (option3, z.B. '1,5 mm')
   * @returns {{ variantId: number, price: string, title: string }|null}
   */
  function resolveVariant({ variants, klasse, currentO2, currentO3 }) {
    if (!variants || !variants.length) return null;

    /* ── 1. O1 bestimmen: PC-Klassen-Match (exakt wie applyFarbtonState) ── */
    let matchedV1 = null;
    if (klasse) {
      const pcTarget = 'PC' + klasse;
      // Suche: v.option1 enthält 'PC1'..'PC4' (z.B. "PC2" oder "Getönt (PC2)")
      matchedV1 = variants.find(
        (v) => v.option1 && v.option1.indexOf(pcTarget) > -1
      );
    }
    if (!matchedV1) {
      // Fallback ohne klasse: PC1-PC4-Match ODER "getönt"-Präfix (case-insensitiv)
      matchedV1 = variants.find(
        (v) =>
          v.option1 &&
          (v.option1.toLowerCase().indexOf('getönt') > -1 ||
            /\bPC\d\b/i.test(v.option1))
      );
    }

    const targetO1 = matchedV1 ? matchedV1.option1 : (variants[0] ? variants[0].option1 : null);

    /* ── 2. findVariant (exakt wie Z.876-882, aber mit option1/option2/option3) ── */
    const find = (o1, o2, o3) =>
      variants.find(
        (v) => v.option1 === o1 && v.option2 === o2 && v.option3 === o3
      ) ||
      variants.find((v) => v.option1 === o1 && v.option2 === o2) ||
      variants.find((v) => v.option1 === o1 && v.option3 === o3) ||
      variants.find((v) => v.option1 === o1) ||
      variants[0];

    const v = find(targetO1, currentO2 || null, currentO3 || null);
    if (!v) return null;

    /* ── 3. Preis formatieren (robust für Cent-Int UND Euro-String/-Float) ── */
    return {
      variantId: v.id,
      price: _formatPrice(v.price),
      title: v.option2 || v.title || '',
    };
  }

  /**
   * Preis-Formatierung mit Format-Heuristik.
   * Verifizierte Datenlage (2026-06-11, StoColor Silco PC4/2.5L):
   *  - .js-Endpunkt: price = Cent-Integer (7145)
   *  - Liquid `product.variants | json` im data-ftk-variants-Script
   *    (Dev-Theme 190784831754): price = ebenfalls Cent-Integer (7145)
   * Heuristik für Fremdformate (z.B. FTKCore-normalisierte Varianten):
   *  - String mit Punkt/Komma   → Euro-Dezimalstring ("71.45" / "71,45" / "1.234,56")
   *  - Zahl mit Nachkommastellen → Euro-Float (71.45)
   *  - Ganzzahl < 1000           → Euro (kein reales Gebinde kostet < 10 € = 1000 Cent)
   *  - sonst                     → Cent-Integer (÷100, KEIN floor/round — exakt)
   * Ausgabe immer via Intl.NumberFormat('de-DE', EUR) → "71,45 €".
   */
  function _formatPrice(p) {
    let euros = null;

    if (typeof p === 'number' && isFinite(p)) {
      if (!Number.isInteger(p)) {
        euros = p; // Float mit Dezimalstellen → bereits Euro
      } else if (p < 1000) {
        euros = p; // kleine Ganzzahl → Euro
      } else {
        euros = p / 100; // Cent-Integer
      }
    } else if (typeof p === 'string') {
      const s = p.trim().replace(/[^\d.,\-]/g, '');
      if (s.length) {
        if (s.indexOf(',') > -1 && s.indexOf('.') > -1) {
          // Deutsches Format mit Tausender-Punkt: "1.234,56"
          euros = parseFloat(s.replace(/\./g, '').replace(',', '.'));
        } else if (s.indexOf(',') > -1) {
          // "71,45" → Komma als Dezimaltrenner
          euros = parseFloat(s.replace(',', '.'));
        } else if (s.indexOf('.') > -1) {
          // "71.45" → Punkt als Dezimaltrenner
          euros = parseFloat(s);
        } else {
          // Reiner Ziffern-String: gleiche Ganzzahl-Heuristik wie oben
          const n = parseInt(s, 10);
          euros = n < 1000 ? n : n / 100;
        }
      }
    }

    if (euros === null || !isFinite(euros)) {
      return String(p); // Unbekanntes Format: roh durchreichen
    }

    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(euros);
  }

  /* ------------------------------------------------------------------ */
  /*  openCartDrawer                                                       */
  /* ------------------------------------------------------------------ */
  function openCartDrawer(btn) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // Overlay-Click → close (nach innerHTML-Replace verloren)
    setTimeout(() => {
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') {
        overlay.addEventListener('click', () => drawer.close());
      }
      if (typeof drawer.open === 'function') {
        drawer.open(btn || null);
      } else {
        // Fallback wenn cart-drawer.js noch nicht upgraded
        drawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  addToCart                                                            */
  /* ------------------------------------------------------------------ */
  /**
   * POST /cart/add.js mit sections.
   * Sections-Antwort → Cart-Drawer + Bubble via DOMParser ersetzen + Drawer öffnen.
   *
   * @param {object} p
   * @param {number}  p.variantId
   * @param {number}  p.qty
   * @param {object}  p.properties   — Shopify line-item properties
   * @param {Element} [p.triggerBtn] — Trigger-Knopf (für drawer.open(btn))
   * @returns {Promise<{ok:boolean, status?:number, error?:string}>}
   */
  async function addToCart({ variantId, qty, properties, triggerBtn }) {
    try {
      const payload = {
        items: [
          {
            id: variantId,
            quantity: qty || 1,
            properties: properties || {},
          },
        ],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        // Shopify-Fehler (z.B. Variante nicht verfügbar)
        return {
          ok: false,
          status: data.status,
          error: data.description || data.message || 'Fehler beim Hinzufügen',
        };
      }

      /* ── Sections-HTML rendern (exakt wie pdp-hero-v2 ~Z.1385-1423) ── */
      if (data.sections) {
        // cart-drawer: ganzes <cart-drawer>-Inhalt ersetzen + is-empty entfernen
        if (data.sections['cart-drawer']) {
          const drawer = document.querySelector('cart-drawer');
          const doc = new DOMParser().parseFromString(
            data.sections['cart-drawer'],
            'text/html'
          );
          const newDrawer = doc.querySelector('cart-drawer');
          if (drawer && newDrawer) {
            drawer.innerHTML = newDrawer.innerHTML;
            drawer.classList.remove('is-empty');
          }
        }
        // cart-icon-bubble: nur den Bubble-Inhalt ersetzen
        if (data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const doc = new DOMParser().parseFromString(
            data.sections['cart-icon-bubble'],
            'text/html'
          );
          const newSection =
            doc.querySelector('.shopify-section') ||
            doc.body.firstElementChild;
          if (bubble && newSection) bubble.innerHTML = newSection.innerHTML;
        }
      }

      // Cart-Drawer öffnen
      openCartDrawer(triggerBtn || null);

      // PubSub für andere Subscriber (cart.js, price-per-item.js etc.)
      try {
        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ftk-atc-v17',
            productVariantId: variantId,
            cartData: data,
          });
        }
      } catch (_) {}

      // Free-Shipping-Push neu berechnen
      if (typeof refreshCartTotal === 'function') refreshCartTotal();

      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Netzwerkfehler' };
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Exports                                                              */
  /* ------------------------------------------------------------------ */
  window.FTKATC = {
    resolveVariant,
    addToCart,
    openCartDrawer,
  };
})();
